import { useEffect } from 'react';
import { mutate } from 'swr';

import { WebSocketConnection, wsUrl } from '@/utils/websocket';

/**
 * One `/fs/watch` socket per sandbox, shared by every hook that wants to know when files
 * change.
 *
 * Previously each caller opened its own: `useFileTree` one, and `useFsWatchRevalidate`
 * one each from `useGitStatus` and `useGitBranchDiff` — which the Changes tab mounts
 * together. That is three sockets and three inotify watchers inside the sandbox for a
 * single page, and when they fail they retry independently, so it also tripled the
 * reconnect traffic.
 *
 * The socket only reports "something changed", so subscribers revalidate rather than
 * reconcile.
 */

interface Watcher {
  conn: WebSocketConnection;
  subscribers: Set<() => void>;
  closeTimer: ReturnType<typeof setTimeout> | null;
}

const watchers = new Map<string, Watcher>();

/** Wait this long at zero subscribers before actually closing. React 19 StrictMode
 *  double-mounts, and switching tabs unmounts one consumer a beat before the next
 *  mounts — without the grace period both would tear the socket down and rebuild it. */
const CLOSE_GRACE_MS = 250;

function subscribeFsWatch(base: string, onChange: () => void): () => void {
  let watcher = watchers.get(base);

  if (!watcher) {
    const subscribers = new Set<() => void>();
    const conn = new WebSocketConnection({
      url: () => wsUrl(`${base}/fs/watch`),
      onMessage: (event) => {
        try {
          const msg = JSON.parse(event.data) as { type: string };
          if (msg.type === 'events') subscribers.forEach((notify) => notify());
        } catch {
          // ignore malformed frames
        }
      },
    });
    watcher = { conn, subscribers, closeTimer: null };
    watchers.set(base, watcher);
  }

  if (watcher.closeTimer) {
    clearTimeout(watcher.closeTimer);
    watcher.closeTimer = null;
  }
  watcher.subscribers.add(onChange);

  return () => {
    const current = watchers.get(base);
    if (!current) return;

    current.subscribers.delete(onChange);
    if (current.subscribers.size > 0) return;

    current.closeTimer = setTimeout(() => {
      // Re-check: a new subscriber may have arrived during the grace period.
      const latest = watchers.get(base);
      if (!latest || latest.subscribers.size > 0) return;
      latest.conn.dispose();
      watchers.delete(base);
    }, CLOSE_GRACE_MS);
  };
}

/** Revalidate `keys` whenever anything under `base` changes on disk. */
export function useFsWatch(base: string | null, keys: string[]) {
  // Serialize for a stable effect dep — a fresh array literal every render would
  // resubscribe on every render.
  const serialized = JSON.stringify(keys);

  useEffect(() => {
    if (!base || !serialized) return;
    return subscribeFsWatch(base, () => {
      (JSON.parse(serialized) as string[]).forEach((key) => mutate(key));
    });
  }, [base, serialized]);
}
